import React, { FC, useState, useEffect } from 'react';
import styled from 'styled-components';
import { Asset, Entry } from 'contentful';
import useContentful from '../../hooks/useContentful';
import ArticlesContainer from './ArticlesContainer';
import Loader from '../Loader';
import contentfulComponentLoop from '../../lib/contentfulComponentLoop';
import CategoryList from './CategoryList';

export interface BlogCategory extends Entry {
  fields: {
    title: string;
    slug: string;
  }
}

export interface BlogTag extends Entry {
  fields: {
    title: string;
    slug: string;
  }
}

export interface Author extends Entry {
  fields: {
    title: string;
    slug: string;
  }
}

export interface ContentfulArticle extends Entry {
  fields: {
    authors?: Author[];
    categories?: BlogCategory[];
    content: string;
    date: string;
    excerpt?: string;
    featuredMedia?: Asset;
    slug: string;
    tags?: BlogTag[];
    title: string;
  };
}

export interface BlogPageTemplateProps {
  fields: {
    title?: string;
    headerBanner?: Entry;
    featuredArticles?: ContentfulArticle[];
    featuredArticlesTitle?: string;
    allArticlesTitle?: string;
    categoriesTitle?: string;
    tagsTitle?: string;
    authorsTitle?: string;
    loadMoreCta?: string;
    backButtonCta?: string;
    shopifyBlogId: string;
  };
}

const LayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  padding: 16px;
  width: 100%;
  max-width: 1200px;
  gap: 32px;
`;

const UpperContent = styled.div`
  margin: 0 auto;
  width: 100%;
  max-width: 1200px;
  overflow: hidden;

  @media (min-width: 768px) {
    padding: 16px;
  }
`;

const LowerContent = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  width: 100%;
  max-width: 1200px;

  @media (min-width: 1024px) {
    flex-direction: row;
    padding: 16px;
  }
`;

const RightColumn = styled.div`
  display: none;
  
  @media (min-width: 1024px) {
    display: block;
    flex: 0 0 300px;
    padding-left: 56px;
  }
`;

const Heading = styled.p`
  color: #000;
  font-size: 32px;
  font-weight: 700;
  line-height: 36px;
  letter-spacing: -0.48px;  
  margin-top: 0;
`;

const StyledButtonPrimary = styled.button`
  width: 112px;
  padding: 12px 24px;
  border-radius: 32px;
  border: 1px solid #000;
  background: #fff;
  color: #000;
  display: flex;
  justify-content: center;
  align-items: center;
  white-space: nowrap;

  &:hover {
    background: #000;
    color: #fff;
    cursor: pointer;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
`;

const articlesPerPage = 10;
const blogElId = 'blog-template-react-target';

interface BlogFilter {
  queryParam: string;
  articleKey: string;
  title: string;
}
const filters: BlogFilter[] = [
  {queryParam: 'cat', articleKey: 'categories', title: 'Category'},
  {queryParam: 'author', articleKey: 'authors', title: 'Author'},
  {queryParam: 'tag', articleKey: 'tags', title: 'Tag'},
];

interface ContentfulQueryArgs {
  content_type: string;
  order: string[];
  include: number;
  limit: number;
  skip: number;
  'metadata.tags.sys.id[in]'?: string;
}

const Blog: FC = () => {
  const blogUrl = document.getElementById(blogElId)?.dataset?.shopifyBlogUrl;
  const contentfulTagId = document.getElementById(blogElId)?.dataset?.contentfulTagId;
  const contentfulBannerId = document.getElementById(blogElId)?.dataset?.contentfulBannerId;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(true);
  const [articles, setArticles] = useState<ContentfulArticle[]>([]);
  
  // Load blog banner
  const {
    contentfulData: bannerQueryResponse, 
    isLoadingContentful: isLoadingBanner
  } = useContentful({"sys.id": contentfulBannerId, include: 10});
  const banner = bannerQueryResponse?.items[0];

  // Load article list
  const queryParams: ContentfulQueryArgs = {
    content_type: 'article',
    order: ['-fields.date'],
    include: 10,
    limit: articlesPerPage,
    skip: (currentPage - 1) * articlesPerPage
  }

  if (contentfulTagId) {
    queryParams['metadata.tags.sys.id[in]'] = contentfulTagId;
  }

  let activeFilter: BlogFilter|undefined;
  let activeFilterContentfulEntryId: string|null;
  filters.forEach(filter => {
    if (!activeFilter && window.location.search && window.location.search.includes(filter.queryParam)) {
      activeFilter = filter;
      const filterContentfulEntryId = new URL(window.location.href).searchParams.get(filter.queryParam);
      activeFilterContentfulEntryId = filterContentfulEntryId;
      // @ts-ignore
      queryParams.links_to_entry = filterContentfulEntryId;
    }
  });

  const { 
    contentfulData: articleData,
    isLoadingContentful: isLoadingArticles 
  } = useContentful(queryParams, JSON.stringify(queryParams));

  useEffect(() => {
    if (articleData && !isLoadingArticles) {
      setArticles(prevArticles => [...prevArticles, ...((articleData.items as unknown) as ContentfulArticle[])]);
      setIsLoadingMore(false);
    }
  }, [articleData, isLoadingArticles]);

  const loadMoreArticles = () => {
    setCurrentPage(prevPage => prevPage + 1);
    setIsLoadingMore(true);
  };

  if (isLoadingBanner || (isLoadingArticles && currentPage == 1) || !articleData) {
    return <Loader />;
  }

  // Get the title of the active filter from the first article data.
  let activeFilterTitle = '';
  if (activeFilter) {
    // @ts-ignore
    articles[0]?.fields?.[activeFilter.articleKey].forEach(item => {
      if (item.sys.id == activeFilterContentfulEntryId) {
        activeFilterTitle = item.fields.title;
      }
    });
  }

  return (
    <LayoutContainer>
      {(contentfulBannerId && banner) && (
        <UpperContent>
          {contentfulComponentLoop([banner])}
        </UpperContent>
      )}
      <LowerContent>
        <div>
          {(activeFilter) && (
            <Heading>{activeFilter.title}: {activeFilterTitle}</Heading>
          )}
          <ArticlesContainer articles={articles} blogUrl={blogUrl} />
          {(isLoadingMore) && <Loader />}
          {(!isLoadingMore && articles.length >= 10) && (
            <ButtonContainer>
              <StyledButtonPrimary onClick={loadMoreArticles}>
                Load More
              </StyledButtonPrimary>
            </ButtonContainer>
          )}
        </div>
        <RightColumn>
          <Heading>Categories</Heading>
          <CategoryList blogUrl={blogUrl} contentfulTagId={contentfulTagId} />
        </RightColumn>
      </LowerContent>
    </LayoutContainer>
  );
};

export default Blog;